// client/src/components/activity/ClassificationExercise.jsx - UPDATED: Peter persona + fixed button colors
import React, { useState, useEffect } from 'react';
import { Button, Card, TextArea } from '../ui/UIComponents';
import { conversationsAPI } from '../../services/api';

// PersonaAvatar Component - Same as ConversationSimulation
const PersonaAvatar = ({ persona, size = 'lg' }) => {
  const { name, avatarUrl } = persona;
  
  const sizeClasses = {
    sm: 'w-12 h-12 text-lg',
    md: 'w-16 h-16 text-2xl', 
    lg: 'w-24 h-24 text-4xl',
    xl: 'w-32 h-32 text-5xl'
  };
  
  if (avatarUrl) {
    return (
      <img 
        src={avatarUrl} 
        alt={name}
        className={`${sizeClasses[size]} rounded-full object-cover shadow-lg`}
      />
    );
  }
  
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold shadow-lg`}>
      {initial}
    </div>
  );
};

// Compact Instruction Box
const CompactInstructionBox = ({ passingCriteria, score, requiredCorrect }) => {
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="text-green-600 text-xl flex-shrink-0">📋</div>
        <div className="flex-1">
          <div className="text-green-900 font-semibold text-sm mb-1">Doel van de oefening:</div>
          <p className="text-green-800 text-sm leading-relaxed">
            {passingCriteria}
          </p>
        </div>
        <div className="flex-shrink-0">
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            score >= requiredCorrect 
              ? 'bg-green-600 text-white' 
              : 'bg-green-100 text-green-700'
          }`}>
            {score}/{requiredCorrect}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassificationExercise = ({ 
  moduleConfig, 
  onComplete, 
  onProgress 
}) => {
  const [currentScreen, setCurrentScreen] = useState('theory');
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Classification specific state
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [awaitingClassification, setAwaitingClassification] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [correctClassification, setCorrectClassification] = useState(null);
  const [score, setScore] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [exerciseCompleted, setExerciseCompleted] = useState(false);
  
  // Theory state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [completedSections, setCompletedSections] = useState(new Set());
  
  // UI state
  const [personaLoading, setPersonaLoading] = useState(false);

  const totalSections = moduleConfig.theory.sections.length;
  const allSectionsCompleted = completedSections.size === totalSections;
  const requiredCorrect = moduleConfig.exercise.requiredCorrect;

  // Parse classification from Claude response
  const parseClassificationResponse = (response) => {
    const classMatch = response.match(/\[CLASSIFICATIE:\s*(VERANDERTAAL|BEHOUDTAAL|NEUTRAAL)\]/);
    const explainMatch = response.match(/\[UITLEG:\s*(.*?)\]/);
    
    if (classMatch) {
      const cleanResponse = response
        .replace(/\[CLASSIFICATIE:.*?\]/, '')
        .replace(/\[UITLEG:.*?\]/, '')
        .trim();
      
      return {
        response: cleanResponse,
        classification: classMatch[1].toLowerCase(),
        explanation: explainMatch ? explainMatch[1] : 'Geen uitleg beschikbaar.'
      };
    }
    
    return {
      response,
      classification: null,
      explanation: null
    };
  };

  // Theory section handlers
  const handleSectionComplete = () => {
    const currentSection = moduleConfig.theory.sections[currentSectionIndex];
    const newCompleted = new Set(completedSections);
    newCompleted.add(currentSection.id);
    setCompletedSections(newCompleted);

    if (onProgress) {
      onProgress({
        sectionsCompleted: newCompleted.size,
        totalSections,
        currentSection: currentSection.id
      });
    }

    const isLastSection = currentSectionIndex === totalSections - 1;
    if (isLastSection) {
      // After theory, go to persona introduction
      startConversation();
    } else {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handleSectionJump = (index) => {
    setCurrentSectionIndex(index);
  };

  // Start conversation - loads Peter persona
  const startConversation = async () => {
    try {
      setIsLoading(true);
      setPersonaLoading(true);
      setError(null);

      const data = await conversationsAPI.start(moduleConfig.id, 1);

      if (!data?.success || !data?.conversationId || !data?.persona) {
        throw new Error('Invalid response from server');
      }

      setConversationId(data.conversationId);
      setSelectedPersona(data.persona);
      setCurrentScreen('persona'); // Show persona introduction
      
    } catch (err) {
      const errorMessage = err.message || 'Unknown error occurred';
      setError(`Kon gesprek niet starten: ${errorMessage}`);
    } finally {
      setIsLoading(false);
      setPersonaLoading(false);
    }
  };

  // Begin the exercise after persona introduction
  const beginExercise = () => {
    setCurrentScreen('exercise-intro');
  };

  // Send message and await classification
  const sendMessage = async () => {
    if (!userMessage.trim() || !conversationId || !selectedPersona) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await conversationsAPI.sendMessage(
        conversationId,
        userMessage.trim()
      );
      
      const parsed = parseClassificationResponse(data.clientResponse);
      
      const newExchange = {
        userMessage: userMessage.trim(),
        clientResponse: parsed.response,
        timestamp: new Date()
      };

      setConversationHistory(prev => [...prev, newExchange]);
      setUserMessage('');
      
      setCurrentResponse(parsed.response);
      setCorrectClassification(parsed.classification);
      setAwaitingClassification(true);

      if (onProgress) {
        onProgress({
          exchangeCount: conversationHistory.length + 1,
          score,
          totalAttempts
        });
      }

    } catch (err) {
      console.error('Message send error:', err);
      if (err.message?.includes('AI_OVERLOADED')) {
        setError('Het AI-systeem is op dit moment niet beschikbaar. Probeer het over een paar seconden opnieuw. Lukt het dan nog niet? Probeer het dan op een later moment — er kan een tijdelijke storing zijn.');
      } else {
        setError('Kon bericht niet versturen. Probeer het opnieuw.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle classification submission
  const handleClassification = (userClassification) => {
    const isCorrect = userClassification === correctClassification;
    const newTotalAttempts = totalAttempts + 1;
    const newScore = isCorrect ? score + 1 : score;
    
    setTotalAttempts(newTotalAttempts);
    setScore(newScore);
    
    let feedback;
    if (isCorrect) {
      feedback = `✅ Correct! Dit is inderdaad ${getCategoryLabel(correctClassification)}.`;
    } else {
      const correctLabel = getCategoryLabel(correctClassification);
      const userLabel = getCategoryLabel(userClassification);
      feedback = `❌ Niet helemaal. Dit is ${correctLabel}, niet ${userLabel}.`;
    }
    
    const parsed = parseClassificationResponse(currentResponse);
    if (parsed.explanation) {
      feedback += ` ${parsed.explanation}`;
    }
    
    setFeedbackMessage(feedback);
    setShowFeedback(true);
    setAwaitingClassification(false);
    
    if (newScore >= requiredCorrect) {
      setExerciseCompleted(true);
      setTimeout(() => {
        onComplete({
          completed: true,
          score: newScore,
          totalAttempts: newTotalAttempts,
          accuracy: Math.round((newScore / newTotalAttempts) * 100)
        });
      }, 2000);
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'verandertaal': return 'verandertaal';
      case 'behoudtaal': return 'behoudtaal';  
      case 'neutraal': return 'neutrale taal';
      default: return category;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'verandertaal': return 'bg-green-500 hover:bg-green-600';
      case 'behoudtaal': return 'bg-red-500 hover:bg-red-600';
      case 'neutraal': return 'bg-blue-500 hover:bg-blue-600';
      default: return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const continueFeedback = () => {
    setShowFeedback(false);
    setFeedbackMessage('');
    setCurrentResponse('');
    setCorrectClassification(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Open theory in new tab
  const openTheoryInNewTab = () => {
    const formatTheoryHTML = (sections) => {
      return sections.map(section => `
        <div style="margin-bottom: 40px;">
          <h2 style="color: #6366f1; margin-bottom: 20px; font-size: 1.5em;">${section.title}</h2>
          ${section.content.split('\n').map(line => {
            const trimmed = line.trim();
            if (!trimmed) return '<br>';
            if (trimmed.includes('**')) {
              const formatted = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              return `<p style="margin-bottom: 15px;">${formatted}</p>`;
            }
            return `<p style="margin-bottom: 15px;">${trimmed}</p>`;
          }).join('')}
        </div>
      `).join('');
    };
    
    const theoryHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Theorie - ${moduleConfig.theory.title}</title>
        <style>
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            max-width: 800px;
            margin: 40px auto;
            padding: 20px;
            line-height: 1.6;
            color: #1f2937;
            background: #f9fafb;
          }
          h1 { 
            color: #7c3aed; 
            margin-bottom: 30px; 
            font-size: 2em;
            font-weight: 600;
          }
          h2 {
            color: #6366f1;
            margin-bottom: 20px;
            font-size: 1.5em;
          }
          p { 
            margin-bottom: 15px; 
            color: #374151;
          }
          strong { 
            color: #4f46e5; 
            font-weight: 600;
          }
        </style>
      </head>
      <body>
        <h1>${moduleConfig.theory.title}</h1>
        ${formatTheoryHTML(moduleConfig.theory.sections)}
      </body>
      </html>
    `;
    
    const blob = new Blob([theoryHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const formatContent = (content) => {
    const lines = content.split('\n');
    const result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed) {
        result.push(null);
        continue;
      }
      
      // Check if this line is a title followed by a URL on the next line
      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      const urlRegex = /^https?:\/\/[^\s]+$/;
      
      if (nextLine && urlRegex.test(nextLine)) {
        // This is a title with URL - format as clickable title
        result.push(
          <div key={i} className="mb-3">
            <a 
              href={nextLine}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline font-medium text-lg"
            >
              {trimmed}
            </a>
          </div>
        );
        i++; // Skip the URL line since we've processed it
        continue;
      }
      
      // Handle headers starting with **
      if (trimmed.includes('**') && trimmed.startsWith('**')) {
        const headerText = trimmed.replace(/\*\*/g, '');
        result.push(
          <h3 key={i} className="font-header text-xl font-medium text-charcoal-800 mt-6 mb-3">
            {headerText}
          </h3>
        );
        continue;
      }
      
      // Handle bold text within paragraphs
      if (trimmed.includes('**')) {
        const parts = trimmed.split('**');
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
            {parts.map((part, partIndex) => 
              partIndex % 2 === 1 ? (
                <strong key={partIndex} className="font-semibold text-charcoal-900">
                  {part}
                </strong>
              ) : (
                part
              )
            )}
          </p>
        );
        continue;
      }
      
      // Handle bullet points
      if (trimmed.startsWith('-')) {
        result.push(
          <li key={i} className="text-charcoal-700 mb-2 ml-4 font-body">
            {trimmed.replace(/^\-\s*/, '')}
          </li>
        );
        continue;
      }
      
      // Check for quotes and format them as italic
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        result.push(
          <p key={i} className="text-charcoal-700 mb-2 leading-relaxed font-body italic">
            {trimmed}
          </p>
        );
        continue;
      }
      
      // Check for URLs and make them clickable
      const urlRegexInline = /(https?:\/\/[^\s]+)/g;
      if (urlRegexInline.test(trimmed)) {
        const parts = trimmed.split(urlRegexInline);
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
            {parts.map((part, partIndex) => {
              if (urlRegexInline.test(part)) {
                return (
                  <a 
                    key={partIndex}
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline font-medium"
                  >
                    {part}
                  </a>
                );
              }
              return part;
            })}
          </p>
        );
        continue;
      }
      
      // Regular paragraphs
      result.push(
        <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
          {trimmed}
        </p>
      );
    }
    
    return result;
  };

  // SCREEN 1: Theory
  if (currentScreen === 'theory') {
    const currentSection = moduleConfig.theory.sections[currentSectionIndex];
    const isLastSection = currentSectionIndex === totalSections - 1;

    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="font-title text-4xl font-light text-purple-600 mb-2">
            {moduleConfig.theory.title}
          </h1>
          <div className="flex items-center justify-center space-x-4 text-sm text-charcoal-600 font-body">
            <span>⏱️ {moduleConfig.duration} minuten</span>
            <span>
              {completedSections.size}/{totalSections} secties voltooid
            </span>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-2">
            {moduleConfig.theory.sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => handleSectionJump(index)}
              className={`flex-1 h-2 rounded-full transition-colors duration-200 ${
                index === currentSectionIndex
                  ? 'bg-purple-600'
                  : completedSections.has(section.id)
                    ? 'bg-green-500'
                    : 'bg-charcoal-300'
              }`}
                title={section.title}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-charcoal-500 font-body">
            <span>Sectie {currentSectionIndex + 1}</span>
            <span>{totalSections} secties totaal</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {moduleConfig.theory.sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => handleSectionJump(index)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 font-body ${
                index === currentSectionIndex
                  ? 'bg-purple-600 text-white'
                  : completedSections.has(section.id)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-cream-200 text-charcoal-600 hover:bg-cream-300'
              }`}
            >
              {completedSections.has(section.id) && '✓ '}
              {section.title}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-container shadow-soft border border-cream-200 p-8 mb-8">
          <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6">
            {currentSection.title}
          </h2>
          
          <div className="prose prose-lg max-w-none">
            {formatContent(currentSection.content)}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => handleSectionJump(Math.max(0, currentSectionIndex - 1))}
            disabled={currentSectionIndex === 0}
          >
            ← Vorige Sectie
          </Button>

          <div className="text-center">
            <p className="text-sm text-charcoal-600 mb-2 font-body">
              Sectie {currentSectionIndex + 1} van {totalSections}
            </p>
            {!completedSections.has(currentSection.id) && (
              <p className="text-xs text-charcoal-500 font-body">
                Klik "Sectie Voltooid" om door te gaan
              </p>
            )}
          </div>

          {!completedSections.has(currentSection.id) ? (
            <Button
              variant="primary"
              onClick={handleSectionComplete}
            >
              {isLastSection ? 'Start Gesprek' : 'Sectie Voltooid'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={isLastSection ? startConversation : () => setCurrentSectionIndex(currentSectionIndex + 1)}
              disabled={isLastSection && (isLoading || personaLoading)}
            >
              {isLastSection ? (isLoading ? 'Voorbereiden...' : 'Start Gesprek') : 'Volgende Sectie →'}
            </Button>
          )}
        </div>

        {/* Loading indicator for persona */}
        {personaLoading && (
          <div className="mt-6 bg-white rounded-container shadow-soft border border-cream-200 p-8 text-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="font-header text-xl font-medium text-charcoal-800 mb-2">Gesprek voorbereiden...</h2>
            <p className="text-charcoal-600 font-body">We laden de gesprekspartner voor deze oefening.</p>
          </div>
        )}

        {/* Show error if persona loading failed */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-red-500 text-xl mr-3">⚠️</div>
              <div className="flex-1">
                <h4 className="font-medium text-red-800 mb-2">Er is een fout opgetreden</h4>
                <p className="text-red-700 text-sm mb-3">{error}</p>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    onClick={startConversation}
                    disabled={isLoading}
                  >
                    Probeer Opnieuw
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setError(null)}
                  >
                    Sluiten
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // SCREEN 2: Persona Introduction (NEW - shows Peter's background)
  if (currentScreen === 'persona' && selectedPersona) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Gesprek met {selectedPersona.name}
          </h2>
          <p className="text-gray-600">
            Herkenningsoefening
          </p>
        </div>

        <Card className="bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="flex justify-center mb-6">
            <PersonaAvatar persona={selectedPersona} size="xl" />
          </div>
          
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            {selectedPersona.name}
          </h3>
          
          <div className="space-y-4">
            <div>
              <span className="font-semibold text-gray-700">Achtergrond:</span>
              <p className="text-gray-600 mt-2 leading-relaxed">
                {selectedPersona.backgroundUser}
              </p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <Button 
              onClick={beginExercise}
              disabled={isLoading}
              size="lg"
            >
              Begin de Oefening
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // SCREEN 3: Exercise Introduction
  if (currentScreen === 'exercise-intro') {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {moduleConfig.exercise.title}
          </h2>
          <p className="text-gray-600">
            Oefen met het herkennen van verandertaal, behoudtaal en neutrale taal
          </p>
        </div>

        <Card className="mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Instructies
          </h3>
          <div className="prose max-w-none">
            <div className="text-gray-700 leading-relaxed">
              {formatContent(moduleConfig.exercise.instruction)}
            </div>
          </div>
        </Card>

        <Card className="mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Categorieën om te herkennen
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {moduleConfig.exercise.categories.map((category) => (
              <div key={category.id} className="text-center p-4 border rounded-lg">
                <div className="text-3xl mb-2">{category.icon}</div>
                <h4 className="font-semibold text-gray-800 mb-2">{category.label}</h4>
                <p className="text-sm text-gray-600">{category.description}</p>
              </div>
            ))}
          </div>
        </Card>

        {error && (
          <Card className="text-center p-8">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Er is een fout opgetreden</h2>
            <div className="text-gray-600 mb-4">
              <p>{error}</p>
            </div>
            <div className="space-x-2">
              <Button onClick={() => window.location.reload()}>
                Pagina verversen
              </Button>
              <Button variant="outline" onClick={() => setError(null)}>
                Probeer opnieuw
              </Button>
            </div>
          </Card>
        )}

        {!error && (
          <Card className="text-center p-8">
            <div className="text-6xl mb-4">💬</div>
            <h3 className="text-xl font-bold text-gray-800 mb-6">
              Klaar om te starten?
            </h3>
            <p className="text-gray-600 mb-6">
              Je gaat een gesprek voeren en classificeert elke uitspraak van de cliënt.
              Je hebt de oefening gehaald bij {requiredCorrect} correcte herkenningen.
            </p>
            <Button 
              onClick={() => setCurrentScreen('conversation')}
              disabled={isLoading}
              size="lg"
            >
              Start Gesprek
            </Button>
          </Card>
        )}
      </div>
    );
  }

  // SCREEN 4: Conversation with Classification - FIXED BUTTON COLORS
  if (currentScreen === 'conversation' && selectedPersona) {
    return (
      <div className="max-w-5xl mx-auto px-4">
        {/* Simplified Header */}
        <div className="flex items-center justify-between mb-6 mt-6">
          <div className="flex items-center gap-3">
            <PersonaAvatar persona={selectedPersona} size="sm" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Gesprek met {selectedPersona.name}
              </h2>
              <p className="text-sm text-gray-600">
                Herkenningsoefening
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={openTheoryInNewTab}
              className="text-gray-600"
            >
              📚 Theorie
            </Button>
          </div>
        </div>

        {/* ALWAYS VISIBLE Compact Instruction Box */}
        <CompactInstructionBox 
          passingCriteria={moduleConfig.exercise.passingCriteria}
          score={score}
          requiredCorrect={requiredCorrect}
        />

        {/* Chat Messages */}
        <Card padding={false} className="mb-4">
          <div className="p-6 space-y-4 min-h-[500px] max-h-[600px] overflow-y-auto">
            {conversationHistory.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-5xl mb-4">👋</div>
                <p className="text-gray-600 text-sm mb-4">
                  Start een natuurlijk gesprek met {selectedPersona.name}.
                </p>
                <p className="text-gray-500 text-xs">
                  Na elke reactie classificeer je de uitspraak als verandertaal, behoudtaal of neutraal.
                </p>
              </div>
            )}
            
            {conversationHistory.map((exchange, index) => (
              <div key={index} className="space-y-4">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="bg-blue-500 text-white rounded-2xl px-6 py-4 max-w-[80%] shadow-sm">
                    <div className="text-xs opacity-90 mb-1">Jij (hulpverlener)</div>
                    <div className="leading-relaxed">{exchange.userMessage}</div>
                  </div>
                </div>
                
                {/* Client response */}
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-800 rounded-2xl px-6 py-4 max-w-[80%] shadow-sm">
                    <div className="text-xs text-gray-600 mb-1">{selectedPersona.name}</div>
                    <div className="leading-relaxed">{exchange.clientResponse}</div>
                  </div>
                </div>
              </div>
            ))}

            {/* Show current response if awaiting classification */}
            {awaitingClassification && currentResponse && (
              <div className="flex justify-start">
                <div className="bg-yellow-100 border-2 border-yellow-300 text-gray-800 rounded-2xl px-6 py-4 max-w-[80%] shadow-sm">
                  <div className="text-xs text-gray-600 mb-1">{selectedPersona.name}</div>
                  <div className="leading-relaxed">{currentResponse}</div>
                  <div className="mt-3 text-center">
                    <div className="text-sm font-medium text-yellow-800 mb-2">
                      👆 Classificeer deze uitspraak:
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Classification buttons (shown when awaiting classification) - FIXED: Use native buttons */}
        {awaitingClassification && (
          <Card className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
              Wat voor soort uitspraak is dit?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {moduleConfig.exercise.categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleClassification(category.id)}
                  disabled={isLoading}
                  className={`${getCategoryColor(category.id)} text-white p-4 rounded-lg h-auto flex flex-col items-center space-y-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5`}
                >
                  <div className="text-2xl">{category.icon}</div>
                  <div className="font-semibold">{category.label}</div>
                  <div className="text-xs opacity-90 text-center">{category.description}</div>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Feedback display */}
        {showFeedback && (
          <Card className="mb-4 bg-blue-50 border-blue-200">
            <div className="text-center">
              <div className="text-lg font-semibold text-blue-800 mb-3">
                {feedbackMessage}
              </div>
              <div className="flex justify-center space-x-2">
                <span className="text-sm text-blue-700">
                  Score: {score}/{requiredCorrect}
                </span>
                <span className="text-sm text-blue-700">
                  • Percentage goed: {totalAttempts > 0 ? Math.round((score / totalAttempts) * 100) : 0}%
                </span>
              </div>
              <Button 
                onClick={continueFeedback}
                className="mt-4"
                size="sm"
              >
                Ga verder met gesprek
              </Button>
            </div>
          </Card>
        )}

        {/* ALWAYS VISIBLE Message Input (hidden during classification) */}
        {!awaitingClassification && !showFeedback && !exerciseCompleted && (
          <Card>
            <div className="space-y-4">
              <TextArea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Typ je bericht aan ${selectedPersona.name}...`}
                rows={3}
                disabled={isLoading}
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Nog {Math.max(0, requiredCorrect - score)} correcte herkenningen nodig
                </div>
                <Button
                  onClick={sendMessage}
                  disabled={!userMessage.trim() || isLoading}
                  size="lg"
                  className="px-8"
                >
                  {isLoading ? 'Versturen...' : 'Verstuur'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Exercise completion */}
        {exerciseCompleted && (
          <Card className="text-center p-8 bg-green-50 border-green-200">
            <div className="text-green-600 text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              Gefeliciteerd!
            </h2>
            <p className="text-green-700 mb-2">
              Je hebt {score} van de {requiredCorrect} uitspraken correct geclassificeerd!
            </p>
            {moduleConfig.exercise.completionText && (
              <p className="text-green-700 mb-4 whitespace-pre-line">
                {moduleConfig.exercise.completionText}
              </p>
            )}
            <div className="text-sm text-green-600">
              Totaal antwoorden goed: {Math.round((score / totalAttempts) * 100)}% ({score}/{totalAttempts})
            </div>
          </Card>
        )}
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="text-center p-8">
        <div className="w-8 h-8 border-3 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Oefening wordt geladen...</p>
      </Card>
    </div>
  );
};

export default ClassificationExercise;
